import type { EquipmentId, EquipmentInfo, PerkId, PerkInfo } from "../../../api/types";
import { LockBadge } from "../../components/Badge";
import { OptionCard } from "../../components/OptionCard";
import { cx } from "../../cx";
import { PERK_COUNT } from "./draft";

interface GearCardProps {
  name: string;
  description: string;
  selected: boolean;
  /** Player level that unlocks it; shown when `locked`. */
  unlockLevel: number;
  locked: boolean;
  /** Not selectable for another reason than a lock. */
  blockedReason?: string;
  role: "radio" | "checkbox";
  onClick: () => void;
}

function GearCard({ name, description, selected, unlockLevel, locked, blockedReason, role, onClick }: GearCardProps) {
  let title = description;
  if (locked) title = `Unlocks at player level ${unlockLevel}`;
  else if (blockedReason) title = blockedReason;
  return (
    <OptionCard
      role={role}
      class={cx("loadouts__gcard", blockedReason && !locked && "is-capped")}
      selected={selected}
      locked={locked || blockedReason !== undefined}
      title={title}
      ariaLabel={`${name}. ${description}${locked ? ` Locked until player level ${unlockLevel}.` : ""}`}
      onClick={onClick}
    >
      <span class="loadouts__gcard-head">
        <span class="card__title loadouts__gcard-name">{name}</span>
        {locked && <LockBadge level={unlockLevel} />}
      </span>
      <span class="loadouts__gcard-desc">{description}</span>
    </OptionCard>
  );
}

export interface EquipmentPickerProps {
  label: string;
  items: readonly EquipmentInfo[];
  selected: EquipmentId;
  unlocked: readonly EquipmentId[];
  onSelect: (id: EquipmentId) => void;
}

/** Radio grid for one equipment kind (lethal or tactical). */
export function EquipmentPicker({ label, items, selected, unlocked, onSelect }: EquipmentPickerProps) {
  return (
    <div class="loadouts__gear" role="radiogroup" aria-label={label}>
      {items.map((item) => (
        <GearCard
          key={item.id}
          role="radio"
          name={item.name}
          description={item.description}
          selected={item.id === selected}
          unlockLevel={item.unlockLevel}
          locked={!unlocked.includes(item.id)}
          onClick={() => onSelect(item.id)}
        />
      ))}
    </div>
  );
}

export interface PerkPickerProps {
  items: readonly PerkInfo[];
  selected: readonly PerkId[];
  unlocked: readonly PerkId[];
  onToggle: (id: PerkId) => void;
}

/** Toggle grid for exactly PERK_COUNT perks; the unselected ones are disabled once the quota is full. */
export function PerkPicker({ items, selected, unlocked, onToggle }: PerkPickerProps) {
  const full = selected.length >= PERK_COUNT;
  return (
    <div class="loadouts__gear" role="group" aria-label="Perks">
      {items.map((perk) => {
        const isSelected = selected.includes(perk.id);
        return (
          <GearCard
            key={perk.id}
            role="checkbox"
            name={perk.name}
            description={perk.description}
            selected={isSelected}
            unlockLevel={perk.unlockLevel}
            locked={!unlocked.includes(perk.id)}
            blockedReason={full && !isSelected ? "Deselect a perk first" : undefined}
            onClick={() => onToggle(perk.id)}
          />
        );
      })}
    </div>
  );
}
